/**
 * PipelineEngineerView — Main entry point for the Pipeline Engineer agent.
 *
 * Internal state machine:
 *   "list"        → PipelineSelect sidebar + instruction card (no pipeline selected)
 *   "create"      → PipelineForm (new pipeline: config + credentials + test, one screen)
 *   "edit"        → PipelineForm (edit existing pipeline: config + credentials + test, one screen)
 *   "job-list"    → PipelineDetailTabs (pipeline selected: Overview / Jobs tabs)
 *   "job-edit"    → JobEditor (job selected for editing)
 *
 * Props accept projectId and onBack explicitly (not only from useParams) so
 * the component is testable without a router context (FR114).
 *
 * Visual design matches the Data Profiling agent page:
 *   - GreenTendrils animated background
 *   - glass-card panel containers
 *   - Tailwind design system tokens only (NFR25, NFR26)
 *   - Page wrapper: relative z-10 max-w-6xl mx-auto p-6 space-y-6 (NFR24)
 */

import { useCallback, useRef, useState } from "react";
import {
  ChevronLeft,
  GitMerge,
  Info,
  Calendar,
  GitBranch,
  History,
} from "lucide-react";
import GreenTendrils from "../../components/GreenTendrils";
import { PipelineSelect } from "../../components/pipeline-engineer/PipelineSelect";
import { PipelineForm } from "../../components/pipeline-engineer/PipelineForm";
import { PipelineDetailTabs } from "../../components/pipeline-engineer/PipelineDetailTabs";
import { JobEditor } from "../../components/pipeline-engineer/JobEditor";
import { SchedulerTab } from "../../components/pipeline-engineer/SchedulerTab";
import { PipelineRunsDashboard } from "../../components/pipeline-engineer/PipelineRunsDashboard";
import { deletePipeline } from "./api/pe-client";
import type { PEPipeline, PEJob } from "./types";

// ---------------------------------------------------------------------------
// View state type
// ---------------------------------------------------------------------------

type ViewMode = "list" | "create" | "edit" | "job-list" | "job-edit";

/** Top-level tab \u2014 "Pipelines" (create/edit/run jobs) vs. "Scheduler"
 *  (configure a pipeline's run schedule, independent of having one open)
 *  vs. "Pipeline Runs" (view run status/history across pipelines, also
 *  independent of having one open). */
type TopTab = "pipelines" | "scheduler" | "runs";

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface PipelineEngineerViewProps {
  /** Project ID — scopes all pipeline operations. */
  projectId: string;
  /** Navigate back to the agent catalog. Optional — component renders without a back handler (FR114). */
  onBack?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function PipelineEngineerView({
  projectId,
  onBack,
}: PipelineEngineerViewProps) {
  const [view, setView] = useState<ViewMode>("list");
  const [topTab, setTopTab] = useState<TopTab>("pipelines");
  const [activePipeline, setActivePipeline] = useState<PEPipeline | null>(null);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  /** Incremented to trigger re-fetch in PipelineSelect after a delete. */
  const [refreshKey, setRefreshKey] = useState(0);
  /** Incremented to trigger re-fetch in JobList after create/delete. */
  const [jobRefreshKey, setJobRefreshKey] = useState(0);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // ── Unsaved-work guard ─────────────────────────────────────────────────────
  // PipelineForm (a pipeline row it created) and JobEditor (dirty tabs) report
  // here; every route out of them that they don't guard themselves — top tabs,
  // the catalog, picking another pipeline — asks first.
  const unsavedRef = useRef(false);
  const handleUnsavedChange = useCallback((hasUnsaved: boolean) => {
    unsavedRef.current = hasUnsaved;
  }, []);
  const confirmLeave = useCallback(
    () =>
      !unsavedRef.current ||
      window.confirm(
        "You have unsaved work that will be lost if you leave. Leave without saving?",
      ),
    [],
  );

  const handleTopTab = (tab: TopTab) => {
    if (tab === topTab || !confirmLeave()) return;
    setTopTab(tab);
  };

  const handleBackToCatalog = () => {
    if (confirmLeave()) onBack?.();
  };

  // ── Pipeline handlers ──────────────────────────────────────────────────────

  const handleSelectPipeline = useCallback(
    (p: PEPipeline) => {
      if (!confirmLeave()) return;
      setActivePipeline(p);
      setActiveJobId(null);
      setView("job-list");
    },
    [confirmLeave],
  );

  const handleNew = useCallback(() => {
    // Already on the blank form: nothing to switch to, and its state is kept.
    if (view === "create" || !confirmLeave()) return;
    setActivePipeline(null);
    setView("create");
  }, [view, confirmLeave]);

  const handleEdit = useCallback(
    (p: PEPipeline) => {
      if (!confirmLeave()) return;
      setActivePipeline(p);
      setView("edit");
    },
    [confirmLeave],
  );

  const handleDelete = useCallback(
    async (p: PEPipeline) => {
      if (
        !window.confirm(
          `Delete pipeline "${p.pipelineName}"? This action cannot be undone.`,
        )
      ) {
        return;
      }
      setDeleteError(null);
      try {
        await deletePipeline(p.id);
        if (activePipeline?.id === p.id) {
          setActivePipeline(null);
          setView("list");
        }
        setRefreshKey((k) => k + 1);
      } catch (err) {
        setDeleteError(
          err instanceof Error ? err.message : "Failed to delete pipeline.",
        );
      }
    },
    [activePipeline],
  );

  const handleFormSaved = useCallback((saved: PEPipeline) => {
    setActivePipeline(saved);
    setRefreshKey((k) => k + 1);
    setView("list");
  }, []);

  const handleFormCancel = useCallback(() => {
    setView("list");
  }, []);

  // ── Job handlers ───────────────────────────────────────────────────────────

  const handleSelectJob = useCallback((job: PEJob) => {
    setActiveJobId(job.id);
    setView("job-edit");
  }, []);

  const handleJobCreated = useCallback((job: PEJob) => {
    setJobRefreshKey((k) => k + 1);
    // Open the editor for the newly created job
    setActiveJobId(job.id);
    setView("job-edit");
  }, []);

  const handleBackToJobList = useCallback(() => {
    setActiveJobId(null);
    setJobRefreshKey((k) => k + 1);
    setView("job-list");
  }, []);

  const handleBackToPipelineList = useCallback(() => {
    if (!confirmLeave()) return;
    setActivePipeline(null);
    setActiveJobId(null);
    setView("list");
  }, [confirmLeave]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="relative min-h-full">
      {/* Animated background */}
      <GreenTendrils />

      {/* Page content */}
      <div className="relative z-10 max-w-6xl mx-auto p-6 space-y-6">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex items-center gap-4">
          {onBack && (
            <button
              type="button"
              onClick={handleBackToCatalog}
              aria-label="Back to agent catalog"
              className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white/80 transition-colors"
            >
              <ChevronLeft size={16} aria-hidden="true" />
              Back
            </button>
          )}
          <div className="flex items-center gap-2">
            <GitMerge size={20} className="text-brand-500" aria-hidden="true" />
            <h1 className="text-lg font-semibold text-white">
              Pipeline Engineer
            </h1>
          </div>
        </div>

        {/* ── Delete error ────────────────────────────────────────────── */}
        {deleteError !== null && (
          <p className="text-red-400 text-sm" role="alert">
            {deleteError}
          </p>
        )}

        {/* ── Top-level tabs ──────────────────────────────────────────── */}
        <div className="flex items-center gap-1 border-b border-white/10">
          <button
            type="button"
            onClick={() => handleTopTab("pipelines")}
            aria-pressed={topTab === "pipelines"}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              topTab === "pipelines"
                ? "border-brand-500 text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            <GitBranch size={14} aria-hidden="true" />
            Pipelines
          </button>
          <button
            type="button"
            onClick={() => handleTopTab("scheduler")}
            aria-pressed={topTab === "scheduler"}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              topTab === "scheduler"
                ? "border-brand-500 text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            <Calendar size={14} aria-hidden="true" />
            Scheduler
          </button>
          <button
            type="button"
            onClick={() => handleTopTab("runs")}
            aria-pressed={topTab === "runs"}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
              topTab === "runs"
                ? "border-brand-500 text-white"
                : "border-transparent text-white/50 hover:text-white/80"
            }`}
          >
            <History size={14} aria-hidden="true" />
            Pipeline Runs
          </button>
        </div>

        {topTab === "scheduler" ? (
          <SchedulerTab projectId={projectId} />
        ) : topTab === "runs" ? (
          <PipelineRunsDashboard projectId={projectId} />
        ) : (
          <>
            {/* ── Main layout ─────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left: Pipeline list (always visible) */}
              <div className="lg:col-span-1">
                <PipelineSelect
                  projectId={projectId}
                  refreshKey={refreshKey}
                  selectedId={activePipeline?.id}
                  onSelect={handleSelectPipeline}
                  onNew={handleNew}
                  onEdit={handleEdit}
                  onDelete={(p) => void handleDelete(p)}
                />
              </div>

              {/* Right: Context panel (changes based on view state) */}
              <div className="lg:col-span-2">
                {view === "list" && (
                  <ListInstructionCard hasSelection={false} />
                )}

                {view === "create" && (
                  <PipelineForm
                    key="create"
                    projectId={projectId}
                    onSaved={handleFormSaved}
                    onCancel={handleFormCancel}
                    onUnsavedChange={handleUnsavedChange}
                  />
                )}

                {view === "edit" && activePipeline !== null && (
                  <PipelineForm
                    key={activePipeline.id}
                    projectId={projectId}
                    pipeline={activePipeline}
                    onSaved={handleFormSaved}
                    onCancel={handleFormCancel}
                    onUnsavedChange={handleUnsavedChange}
                  />
                )}

                {view === "job-list" && activePipeline !== null && (
                  <div className="space-y-4">
                    {/* Pipeline info bar */}
                    <div className="flex items-center gap-3 px-1">
                      <button
                        type="button"
                        onClick={handleBackToPipelineList}
                        className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white/80 transition-colors"
                      >
                        <ChevronLeft size={13} aria-hidden="true" />
                        Pipelines
                      </button>
                      <span className="text-white/20" aria-hidden="true">
                        /
                      </span>
                      <span className="text-xs font-medium text-white/70">
                        {activePipeline.pipelineName}
                      </span>
                    </div>

                    <PipelineDetailTabs
                      pipeline={activePipeline}
                      projectId={projectId}
                      refreshKey={jobRefreshKey}
                      selectedJobId={activeJobId ?? undefined}
                      onSelectJob={handleSelectJob}
                      onJobCreated={handleJobCreated}
                    />
                  </div>
                )}

                {view === "job-edit" &&
                  activePipeline !== null &&
                  activeJobId !== null && (
                    <JobEditor
                      pipelineId={activePipeline.id}
                      jobId={activeJobId}
                      projectId={projectId}
                      pipeline={activePipeline}
                      onBack={handleBackToJobList}
                      onUnsavedChange={handleUnsavedChange}
                    />
                  )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component
// ---------------------------------------------------------------------------

function ListInstructionCard({
  hasSelection: _hasSelection,
}: {
  hasSelection: boolean;
}) {
  return (
    <div className="glass-card rounded-2xl p-8 flex flex-col items-center justify-center text-center space-y-4 min-h-48">
      <div className="h-12 w-12 rounded-full bg-brand-500/10 flex items-center justify-center">
        <Info size={24} className="text-brand-400" aria-hidden="true" />
      </div>
      <h3 className="text-base font-semibold text-white">Select a pipeline</h3>
      <p className="text-sm text-white/50 max-w-sm">
        Choose an existing pipeline from the list to view and edit its jobs, or
        click <strong className="text-white/70">New Pipeline</strong> to
        configure a new source-to-target connection.
      </p>
    </div>
  );
}
